import { track } from "@/lib/analytics";
import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { RevealEmail } from "@/components/site/RevealEmail";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const inputCls =
  "mt-2 w-full rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-3 text-base text-fg placeholder:text-fg-3/80 outline-none transition-colors focus:border-signal";

export const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="flex items-baseline justify-between gap-3">
      <span className="eyebrow text-fg-3">{label}{required && <span className="text-signal"> *</span>}</span>
      {hint && <span className="text-[12px] text-fg-3">{hint}</span>}
    </span>
    {children}
  </label>
);

export const TextInput = ({ label, required, hint, testId, ...props }) => (
  <Field label={label} required={required} hint={hint}>
    <input className={inputCls} required={required} data-testid={testId} {...props} />
  </Field>
);

export const TextArea = ({ label, required, hint, testId, rows = 5, ...props }) => (
  <Field label={label} required={required} hint={hint}>
    <textarea className={`${inputCls} resize-y leading-relaxed`} rows={rows} required={required} data-testid={testId} {...props} />
  </Field>
);

export const Chips = ({ label, options, value, onChange, multi = false, required, testIdPrefix }) => (
  <fieldset>
    <legend className="eyebrow text-fg-3">{label}{required && <span className="text-signal"> *</span>}</legend>
    <div className="mt-3 flex flex-wrap gap-2">
      {options.map((o) => {
        const on = multi ? value.includes(o) : value === o;
        const next = multi ? (on ? value.filter((v) => v !== o) : [...value, o]) : o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(next)}
            aria-pressed={on}
            className={`rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-300 ${on ? "border-signal bg-signal/10 text-fg" : "border-white/15 text-fg-2 hover:border-white/35 hover:text-fg"}`}
            data-testid={`${testIdPrefix}-${slug(o)}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export const Honeypot = ({ value, onChange }) => (
  <input
    type="text"
    name="company_website"
    tabIndex={-1}
    autoComplete="off"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
    aria-hidden="true"
  />
);

export const SentPanel = ({ reference, onReset, testId = "form-success" }) => (
  <div role="status" className="rounded-3xl border border-signal/30 bg-signal/[0.04] p-8 sm:p-10" data-testid={testId} data-clarity-mask="true">
    <span className="grid h-12 w-12 place-items-center rounded-full bg-signal text-ink"><Check className="h-5 w-5" /></span>
    <h3 className="mt-6 text-3xl font-[560] tracking-[-0.03em] text-fg">Received.</h3>
    <p className="mt-3 max-w-md text-[15px] leading-relaxed text-fg-2">Thank you. A founder reads every message and will reply from our own inbox.</p>
    {reference && <p className="mono mt-5 text-[13px] text-fg-3" data-testid={`${testId}-reference`}>Reference {reference}</p>}
    <button type="button" onClick={onReset} className="mt-6 text-sm text-fg underline decoration-white/30 underline-offset-4 hover:decoration-signal" data-testid={`${testId}-reset`}>Send another</button>
  </div>
);

export const FormError = ({ message, testId = "form-error" }) =>
  message ? (
    <div role="alert" className="rounded-2xl border border-caution/40 bg-caution/[0.06] p-4 text-[14px] text-fg" data-testid={testId}>
      <p>{message}</p>
      <div className="mt-3"><RevealEmail label="Or reach the founders directly: show email" testId={`${testId}-email`} /></div>
    </div>
  ) : null;

export const useEnquiry = (endpoint) => {
  const [state, setState] = useState({ status: "idle", reference: null, error: null });
  const attempt = useRef(null);
  const kind = { "/enquiries/partner": "partner", "/enquiries/application": "application", "/enquiries/pratham": "pratham" }[endpoint];
  const submit = async (payload) => {
    track(`${kind}_submit`);
    const serialized = JSON.stringify(payload);
    if (attempt.current?.payload !== serialized) {
      attempt.current = { payload: serialized, key: crypto.randomUUID() };
    }
    setState({ status: "sending", reference: null, error: null });
    try {
      const res = await api.post(endpoint, payload, { headers: { "Idempotency-Key": attempt.current.key } });
      if (res.data?.ok !== true || typeof res.data.reference !== "string") throw new Error("Invalid submission response");
      track(`${kind}_success`);
      setState({ status: "sent", reference: res.data?.reference ?? null, error: null });
    } catch (err) {
      track(`${kind}_error`);
      setState({ status: "error", reference: null, error: errorMessage(err) });
    }
  };
  const reset = () => {
    attempt.current = null;
    setState({ status: "idle", reference: null, error: null });
  };
  return { ...state, submit, reset };
};
